import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAllShows } from "../hooks/useShows";
import LogCard from "../components/LogCard";
import NotificationBell from "../components/NotificationBell";
import { getFeed } from "../lib/services";

export default function Home() {
  const [tab, setTab] = useState("toronto");
  const [following, setFollowing] = useState({ items: [], count: 0 });
  const [toronto, setToronto] = useState({ items: [], count: 0 });
  const [loaded, setLoaded] = useState(false);
  const allShows = useAllShows();

  const refresh = useCallback(async () => {
    try {
      const [f, t] = await Promise.all([
        getFeed("following"),
        getFeed("toronto"),
      ]);
      setFollowing(f);
      setToronto(t);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onLogDeleted = (deletedId) => {
    const filt = (s) => ({
      items: s.items.filter((it) => it.log?.id !== deletedId),
      count: Math.max(0, (s.count || 0) - 1),
    });
    setFollowing((f) => filt(f));
    setToronto((t) => filt(t));
  };

  const feed = tab === "following" ? following : toronto;

  return (
    <main data-testid="home-page" className="min-h-screen bg-black text-white pb-28">
      <header className="px-5 pt-6 pb-4 border-b-2 border-white/15">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              feed
            </div>
            <h1 className="font-display text-5xl leading-none mt-1">
              home<span className="text-acid">.</span>
            </h1>
          </div>
          <NotificationBell className="text-white/70 hover:text-acid mt-2" />
        </div>
      </header>

      {/* shortcut tiles */}
      <section className="grid grid-cols-2 border-b-2 border-white/15">
        <Link
          to="/toronto"
          data-testid="home-cta-toronto"
          className="px-5 py-5 border-r border-white/15 hover:bg-acid/10 transition-colors"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest text-acid">
            browse
          </div>
          <div className="font-display text-xl leading-tight mt-1">
            toronto
          </div>
          <div className="font-mono text-[11px] text-white/50 mt-1">
            {allShows.length} shows
          </div>
        </Link>
        <Link
          to="/log"
          className="px-5 py-5 hover:bg-acid/10 transition-colors"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest text-acid">
            log
          </div>
          <div className="font-display text-xl leading-tight mt-1">
            log a show
          </div>
          <div className="font-mono text-[11px] text-white/50 mt-1">
            add to your diary
          </div>
        </Link>
      </section>

      {/* feed tabs */}
      <nav data-testid="home-tabs" className="grid grid-cols-2 border-b-2 border-white/15">
        <button
          data-testid="tab-following"
          onClick={() => setTab("following")}
          className={`py-3 font-mono text-xs uppercase tracking-wider transition-colors ${
            tab === "following" ? "bg-acid text-black" : "text-white/70 hover:text-white"
          }`}
        >
          following <span className={`tabular-nums ${tab === "following" ? "text-black/70" : "text-white/40"}`}>({following.count})</span>
        </button>
        <button
          data-testid="tab-toronto"
          onClick={() => setTab("toronto")}
          className={`py-3 font-mono text-xs uppercase tracking-wider border-l border-white/15 transition-colors ${
            tab === "toronto" ? "bg-acid text-black" : "text-white/70 hover:text-white"
          }`}
        >
          toronto <span className={`tabular-nums ${tab === "toronto" ? "text-black/70" : "text-white/40"}`}>({toronto.count})</span>
        </button>
      </nav>

      <section data-testid="home-feed">
        {!loaded ? (
          <div className="px-5 py-10 text-center font-mono text-xs text-white/40">loading...</div>
        ) : feed.items.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <p className="font-mono text-xs text-white/40">
              {tab === "following"
                ? "follow people to see their logs here."
                : "no public logs yet. be the first."}
            </p>
          </div>
        ) : (
          <ul>
            {feed.items.map((item) => (
              <li
                key={item.log.id}
                data-testid={`feed-item-${item.log.id}`}
                className="border-t border-white/10 px-5 py-5"
              >
                <LogCard
                  log={item.log}
                  show={item.show}
                  user={item.user}
                  showUser={!!item.user}
                  onDeleted={onLogDeleted}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
